import { EMPTY_STRING, SUSPICIOUS_NAME_TOKENS } from "src/components/constant/constant";

export const formatCurrency = (amount: number, currency: string = "AUD"): string => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount);
};

export function convertToCSV<T extends Record<string, any>>(data: T[]): string {
  if (!data || !data.length) return EMPTY_STRING;
  const keys = Object.keys(data[0]);
  const escape = (value: any) => {
    if (value == null) return EMPTY_STRING;
    let str = String(value);

    if (str.includes('"')) str = str.replace(/"/g, '""');
    if (str.search(/[",\n]/g) >= 0) str = `"${str}"`;
    return str;
  };
  const header = keys.join(",");
  const rows = data.map((row) => keys.map((k) => escape(row[k])).join(","));

  return [header, ...rows].join("\n");
}

export const formatToRunningCase = (input: string | null | undefined): string => {
  if (!input) {
    return EMPTY_STRING;
  }

  return input
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatInitial = (initial: string | undefined | null): string => {
  if (!initial) return "Mr.";
  return initial.endsWith(".") ? initial : `${initial}.`;
};

const isSuspiciousNameToken = (token: string): boolean => {
  const lower = token.toLowerCase();

  return SUSPICIOUS_NAME_TOKENS.some((suspicious) => suspicious === lower);
};

export const formatFullName = (first?: string | null, last?: string | null): string => {
  const parts = [first, last]
    .map((part) => (part ?? EMPTY_STRING).trim())
    .flatMap((part) => part.split(/\s+/))
    .filter((part) => part.length > 0 && !isSuspiciousNameToken(part));

  return parts.join(" ");
};

export const getOrdinalSuffix = (number: number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = number % 100;

  return suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0];
};

export const getDecimalNumber = (amount: number) => {
  return Math.abs(parseFloat(amount?.toFixed(3)));
};

/** `fractionDigits` pins the decimals (e.g. 2 for `$9.50`); omitted, trailing zeros are trimmed. */
export const formatSignedDollar = (amount?: number, fractionDigits?: number) => {
  const value = amount ?? 0;
  const sign = value < 0 ? "-" : EMPTY_STRING;
  const magnitude = Math.abs(value);

  return `${sign}$${fractionDigits === undefined ? getDecimalNumber(magnitude) : magnitude.toFixed(fractionDigits)}`;
};

export const balanceTone = (balance?: number): string | undefined => {
  if (!balance) return undefined;
  if (balance > 0) return "#15803d";
  if (balance < 0) return "#b91c1c";

  return undefined;
};

// "A", "A and B", "A, B and 2 others" — names are never cut, the tail is counted.
export const summariseList = (labels: string[]): string => {
  if (labels.length <= 2) return labels.join(" and ");
  const others = labels.length - 2;

  return `${labels[0]}, ${labels[1]} and ${others} ${others === 1 ? "other" : "others"}`;
};

export const formatXeroTaxTypeLabel = (taxType?: { taxType?: string; name?: string } | null): string => {
  const code = taxType?.taxType ?? EMPTY_STRING;
  const name = taxType?.name ?? EMPTY_STRING;

  if (!code) return EMPTY_STRING;

  return name && name !== code ? `${code} (${name})` : code;
};
